import { useEffect, useMemo, useRef } from 'react';
import { TextInput, type TextInputProps } from '@mantine/core';
import { RichTextEditor } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextEntryHistoryButtons } from './TextEntryUndoButton';
import { useQuestionnaireTextUndo } from './useQuestionnaireTextUndo';
import './QuestionnaireTextEntry.css';

export type QuestionnaireTextEntryMode = 'rich' | 'plain';

export interface QuestionnaireTextEntryProps {
  fieldKey: string;
  mode: QuestionnaireTextEntryMode;
  value: string;
  onChange: (next: string) => void;
  /** Passed to plain TextInput or rich editor root styles. */
  styles?: TextInputProps['styles'];
  placeholder?: string;
}

function useHistoryKeyboard(
  containerRef: React.RefObject<HTMLElement | null>,
  undo: () => boolean,
  redo: () => boolean,
): void {
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (!isMod || e.key.toLowerCase() !== 'z') return;
      if (e.shiftKey) {
        if (redo()) {
          e.preventDefault();
          e.stopPropagation();
        }
      } else if (undo()) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    el.addEventListener('keydown', onKeyDown);
    return () => el.removeEventListener('keydown', onKeyDown);
  }, [containerRef, undo, redo]);
}

/** Shared questionnaire text input with undo/redo (typing + AI replace). */
export function QuestionnaireTextEntry({
  fieldKey,
  mode,
  value,
  onChange,
  styles,
  placeholder,
}: QuestionnaireTextEntryProps): JSX.Element {
  const { displayValue, commitUserEdit, undo, redo, canUndo, canRedo } = useQuestionnaireTextUndo({
    fieldKey,
    value,
    onChange,
  });

  if (mode === 'plain') {
    return (
      <PlainQuestionnaireTextEntry
        displayValue={displayValue}
        commitUserEdit={commitUserEdit}
        undo={undo}
        redo={redo}
        canUndo={canUndo}
        canRedo={canRedo}
        styles={styles}
        placeholder={placeholder}
      />
    );
  }

  return (
    <RichQuestionnaireTextEntry
      displayValue={displayValue}
      commitUserEdit={commitUserEdit}
      undo={undo}
      redo={redo}
      canUndo={canUndo}
      canRedo={canRedo}
      styles={styles}
      placeholder={placeholder}
    />
  );
}

function PlainQuestionnaireTextEntry({
  displayValue,
  commitUserEdit,
  undo,
  redo,
  canUndo,
  canRedo,
  styles,
  placeholder,
}: {
  displayValue: string;
  commitUserEdit: (next: string) => void;
  undo: () => boolean;
  redo: () => boolean;
  canUndo: boolean;
  canRedo: boolean;
  styles?: TextInputProps['styles'];
  placeholder?: string;
}): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  useHistoryKeyboard(containerRef, undo, redo);

  return (
    <div ref={containerRef}>
      <TextInput
        value={displayValue}
        onChange={(e) => commitUserEdit(e.currentTarget.value)}
        styles={styles}
        placeholder={placeholder}
        rightSection={
          <TextEntryHistoryButtons
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={undo}
            onRedo={redo}
          />
        }
        rightSectionWidth={68}
      />
    </div>
  );
}

function RichQuestionnaireTextEntry({
  displayValue,
  commitUserEdit,
  undo,
  redo,
  canUndo,
  canRedo,
  styles,
  placeholder,
}: {
  displayValue: string;
  commitUserEdit: (next: string) => void;
  undo: () => boolean;
  redo: () => boolean;
  canUndo: boolean;
  canRedo: boolean;
  styles?: TextInputProps['styles'];
  placeholder?: string;
}): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  useHistoryKeyboard(containerRef, undo, redo);

  // Tiptap v3 StarterKit already bundles Underline and Link — adding them
  // separately registers duplicate marks/plugins and degrades typing perf.
  const extensions = useMemo(
    () => [
      StarterKit.configure({
        undoRedo: false,
        link: { openOnClick: false },
      }),
    ],
    [],
  );

  /** Last HTML we emitted to the parent — lets us skip the expensive
   * getHTML()/setContent round-trip when the prop change is our own edit. */
  const lastEmittedHtmlRef = useRef<string | null>(null);

  const editor = useEditor({
    extensions,
    content: displayValue || '',
    // Re-render this leaf component on each transaction so toolbar controls
    // reflect active marks immediately (v3 defaults to false → stale state).
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor: e }) => {
      const html = e.getHTML();
      lastEmittedHtmlRef.current = html;
      commitUserEdit(html);
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (displayValue === lastEmittedHtmlRef.current) return;
    lastEmittedHtmlRef.current = displayValue;
    if (editor.getHTML() !== displayValue) {
      editor.commands.setContent(displayValue || '', { emitUpdate: false });
    }
  }, [displayValue, editor]);

  // placeholder reserved for TipTap Placeholder extension / CSS empty-state
  void placeholder;

  const richRootStyle =
    styles && typeof styles !== 'function' ? styles.input : undefined;

  return (
    <div ref={containerRef} className="questionnaire-rich-text-entry">
      <RichTextEditor
        editor={editor}
        styles={{
          root: {
            ...richRootStyle,
            border: '1px solid var(--mantine-color-gray-4)',
            borderRadius: 'var(--mantine-radius-md)',
            overflow: 'hidden',
            backgroundColor: 'var(--mantine-color-white)',
          },
          content: {
            backgroundColor: 'var(--mantine-color-white)',
            minHeight: 96,
          },
        }}
      >
        <RichTextEditor.Content />
        <RichTextEditor.Toolbar
          className="questionnaire-rich-text-toolbar"
          onMouseDown={(e) => e.preventDefault()}
        >
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Bold />
            <RichTextEditor.Italic />
            <RichTextEditor.Underline />
            <RichTextEditor.Strikethrough />
          </RichTextEditor.ControlsGroup>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.BulletList />
            <RichTextEditor.OrderedList />
          </RichTextEditor.ControlsGroup>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Link />
            <RichTextEditor.Unlink />
          </RichTextEditor.ControlsGroup>
          <RichTextEditor.ControlsGroup>
            <TextEntryHistoryButtons
              canUndo={canUndo}
              canRedo={canRedo}
              onUndo={undo}
              onRedo={redo}
            />
          </RichTextEditor.ControlsGroup>
        </RichTextEditor.Toolbar>
      </RichTextEditor>
    </div>
  );
}
